import { createSlice, PayloadAction } from "@reduxjs/toolkit";

// ─── Shared multilingual helpers ────────────────────────
export type MultilingualText = Record<string, string>;
export type MultilingualPair = Record<
    string,
    { title: string; description: string }
>;

// ─── Activity ───────────────────────────────────────────
export type ActivityId =
    | "fastfood"
    | "traditional_restaurant"
    | "cafe"
    | "hotel"
    | "camping";

export type ActivityConfig = {
    selectedActivity: ActivityId;
    appName: string;
};

// ─── Banner ─────────────────────────────────────────────
export type BannerImageItem = {
    id: number;
    order: number;
    filename: string;
    url?: string;
    type: "image" | "video";
    active: boolean;
    activityId: string;
    activityLabel: string;
};

export type BannerImages = {
    Mobile: BannerImageItem[];
    Desktop: BannerImageItem[];
};

export type BannerConfig = {
    bannerEnabled: boolean;
    titleVisible: boolean;
    descriptionVisible: boolean;
    titleColor: string;
    descriptionColor: string;
    displayType: string;
    customTexts: MultilingualPair;
    images: BannerImages;
};

// ─── Offers ─────────────────────────────────────────────
export type OfferItem = {
    id: number;
    order: number;
    image: string;
    imageUrl?: string;
    title: MultilingualText;
    description: MultilingualText;
    tag: string;
    url: string;
    active: boolean;
};

export type OfferImages = {
    Mobile: OfferItem[];
    Desktop: OfferItem[];
};

export type SectionIconKey =
    | "tag"
    | "star"
    | "gift"
    | "percent"
    | "fire"
    | "heart"
    | "flash"
    | "diamond";

export type OffersConfig = {
    offersEnabled: boolean;
    titleVisible: boolean;
    selectedIcon: SectionIconKey | "custom";
    customIconUrl: string;
    sectionTitles: MultilingualText;
    offers: OfferImages;
};

// ─── Actions ────────────────────────────────────────────
export type ActionItem = {
    id: number;
    order: number;
    icon: string;
    iconColor: string;
    title: MultilingualText;
    description: MultilingualText;
    titleVisible: boolean;
    descriptionVisible: boolean;
    active: boolean;
};

export type ActionsConfig = {
    actionsEnabled: boolean;
    sectionTitleVisible: boolean;
    selectedIcon: SectionIconKey | "custom";
    customIconUrl: string;
    sectionTitles: MultilingualText;
    actions: ActionItem[];
};

// ─── My Order ───────────────────────────────────────────
export type OrderStatusKey = "ordered" | "preparing" | "ready" | "served";

export type OrderStatus = {
    key: OrderStatusKey;
    active: boolean;
    labels: MultilingualText;
};

export type MyOrderConfig = {
    orderEnabled: boolean;
    sectionTitleVisible: boolean;
    selectedIcon: string;
    customIconUrl: string;
    showStepper: boolean;
    showOrderDetails: boolean;
    previewMode: "with_order" | "no_order";
    sectionTitles: MultilingualText;
    orderStatuses: OrderStatus[];
};

// ─── Advertising ────────────────────────────────────────
export type AdItem = {
    id: number;
    order: number;
    filename: string;
    imageUrl: string;
    active: boolean;
};

export type AdImages = {
    Mobile: AdItem[];
    Desktop: AdItem[];
};

export type AdvertisingConfig = {
    adsEnabled: boolean;
    sectionTitleVisible: boolean;
    selectedIcon: SectionIconKey | "custom";
    customIconUrl: string;
    displayType: string;
    sectionTitles: MultilingualText;
    ads: AdImages;
};

// ─── Miscellaneous Settings ─────────────────────────────
export type SocialChannelId =
    | "instagram"
    | "facebook"
    | "tiktok"
    | "twitter"
    | "linkedin"
    | "youtube";

export type SocialChannel = {
    id: SocialChannelId;
    enabled: boolean;
    url: string;
};

export type MiscellaneousConfig = {
    primaryColor: string;
    secondaryColor: string;
    nameVisible: boolean;
    descriptionVisible: boolean;
    restaurantTexts: MultilingualPair;
    showSocialNetworks: boolean;
    socialChannels: SocialChannel[];
    showLegalInfo: boolean;
    enabledLanguages: Record<string, boolean>;
    defaultLanguage: string;
};

// ─── Payment Options ────────────────────────────────────
export type PaymentMethod = {
    id: string;
    icon: string;
    title: MultilingualText;
    description: MultilingualText;
    enabled: boolean;
};

export type SplitOption = {
    id: string;
    icon: string;
    title: MultilingualText;
    description: MultilingualText;
    enabled: boolean;
};

export type PaymentConfig = {
    paymentEnabled: boolean;
    paymentMethods: PaymentMethod[];
    splitPaymentEnabled: boolean;
    splitOptions: SplitOption[];
    methodDescVisible: Record<string, boolean>;
    splitDescVisible: Record<string, boolean>;
};

// ─── Server Call Options ────────────────────────────────
export type QuickOption = {
    id: string;
    icon: string;
    customIconUrl?: string;
    title: MultilingualText;
    isDefault: boolean;
    enabled: boolean;
};

export type ServerCallConfig = {
    serverCallEnabled: boolean;
    customMessageEnabled: boolean;
    quickOptions: QuickOption[];
};

// Single language the whole preview renders in. Any rubric's language
// picker updates this, so the preview stays consistent across sections
// instead of each rubric showing its own last-picked language.
const DEFAULT_PREVIEW_LANGUAGE = "fr";

// ─── Full State ─────────────────────────────────────────
export type ScanToState = {
    isEdited: boolean;
    activeTab: number;
    // Shared preview language — see DEFAULT_PREVIEW_LANGUAGE.
    previewLanguage: string;
    activity: ActivityConfig;
    banner: BannerConfig;
    offers: OffersConfig;
    actions: ActionsConfig;
    myOrder: MyOrderConfig;
    advertising: AdvertisingConfig;
    miscellaneousSettings: MiscellaneousConfig;
    paymentOptions: PaymentConfig;
    serverCallOptions: ServerCallConfig;
};

// ─── Initial State ──────────────────────────────────────
const initialState: ScanToState = {
    isEdited: false,
    activeTab: 0,
    previewLanguage: DEFAULT_PREVIEW_LANGUAGE,

    activity: {
        selectedActivity: "fastfood",
        appName: "eat",
    },

    banner: {
        bannerEnabled: true,
        titleVisible: true,
        descriptionVisible: true,
        titleColor: "#ffffff",
        descriptionColor: "#ffffff",
        displayType: "desktop",
        customTexts: {
            fr: { title: "Bienvenue", description: "Scannez pour commander" },
            en: { title: "Welcome", description: "Scan to order" },
            ru: {
                title: "Добро пожаловать",
                description: "Сканируйте для заказа",
            },
            it: { title: "Benvenuto", description: "Scansiona per ordinare" },
            es: { title: "Bienvenido", description: "Escanee para pedir" },
            pt: {
                title: "Bem-vindo",
                description: "Digitalize para encomendar",
            },
            de: {
                title: "Willkommen",
                description: "Scannen Sie, um zu bestellen",
            },
            ar: { title: "مرحبا", description: "امسح للطلب" },
        },
        images: {
            Mobile: [],
            Desktop: [
                {
                    id: 1,
                    order: 1,
                    filename: "banner_fastfood_1.jpg",
                    url: "",
                    type: "image",
                    active: true,
                    activityId: "fastfood",
                    activityLabel: "Fastfood",
                },
                {
                    id: 2,
                    order: 2,
                    filename: "banner_fastfood_2.jpg",
                    url: "",
                    type: "image",
                    active: true,
                    activityId: "fastfood",
                    activityLabel: "Fastfood",
                },
                {
                    id: 3,
                    order: 3,
                    filename: "banner_restaurant_1.jpg",
                    url: "",
                    type: "image",
                    active: false,
                    activityId: "traditional_restaurant",
                    activityLabel: "Traditional restaurant",
                },
                {
                    id: 4,
                    order: 4,
                    filename: "banner_restaurant_2.jpg",
                    url: "",
                    type: "image",
                    active: false,
                    activityId: "traditional_restaurant",
                    activityLabel: "Traditional restaurant",
                },
                {
                    id: 5,
                    order: 5,
                    filename: "banner_cafe_1.jpg",
                    url: "",
                    type: "image",
                    active: false,
                    activityId: "cafe",
                    activityLabel: "Café",
                },
                {
                    id: 6,
                    order: 6,
                    filename: "banner_cafe_2.jpg",
                    url: "",
                    type: "image",
                    active: false,
                    activityId: "cafe",
                    activityLabel: "Café",
                },
                {
                    id: 7,
                    order: 7,
                    filename: "banner_hotel_1.jpg",
                    url: "",
                    type: "image",
                    active: false,
                    activityId: "hotel",
                    activityLabel: "Hotel",
                },
                {
                    id: 8,
                    order: 8,
                    filename: "banner_hotel_2.jpg",
                    url: "",
                    type: "image",
                    active: false,
                    activityId: "hotel",
                    activityLabel: "Hotel",
                },
                {
                    id: 9,
                    order: 9,
                    filename: "banner_camping_1.jpg",
                    url: "",
                    type: "image",
                    active: false,
                    activityId: "camping",
                    activityLabel: "Camping",
                },
                {
                    id: 10,
                    order: 10,
                    filename: "banner_camping_2.jpg",
                    url: "",
                    type: "image",
                    active: false,
                    activityId: "camping",
                    activityLabel: "Camping",
                },
            ],
        },
    },

    offers: {
        offersEnabled: true,
        titleVisible: true,
        selectedIcon: "tag",
        customIconUrl: "",
        sectionTitles: {
            fr: "Offres du jour",
            en: "Offers of the day",
            ru: "Предложения дня",
            it: "Offerte del giorno",
            es: "Ofertas del día",
            pt: "Ofertas do dia",
            de: "Angebote des Tages",
            ar: "عروض اليوم",
        },
        offers: { Mobile: [], Desktop: [] },
    },

    actions: {
        actionsEnabled: true,
        sectionTitleVisible: true,
        selectedIcon: "tag",
        customIconUrl: "",
        sectionTitles: {
            fr: "Que souhaitez-vous faire ?",
            en: "What would you like to do?",
            ru: "Что вы хотите сделать?",
            it: "Cosa desideri fare?",
            es: "¿Qué desea hacer?",
            pt: "O que deseja fazer?",
            de: "Was möchten Sie tun?",
            ar: "ماذا تريد أن تفعل؟",
        },
        actions: [],
    },

    myOrder: {
        orderEnabled: true,
        sectionTitleVisible: true,
        selectedIcon: "cart",
        customIconUrl: "",
        showStepper: true,
        showOrderDetails: true,
        previewMode: "with_order",
        sectionTitles: {
            fr: "Ma commande",
            en: "My order",
            ru: "Мой заказ",
            it: "Il mio ordine",
            es: "Mi pedido",
            pt: "Minha encomenda",
            de: "Meine Bestellung",
            ar: "طلبي",
        },
        orderStatuses: [
            {
                key: "ordered",
                active: true,
                labels: {
                    fr: "Commandé",
                    en: "Ordered",
                    es: "Pedido",
                    it: "Ordinato",
                    pt: "Encomendado",
                    de: "Bestellt",
                    ru: "Заказано",
                    ar: "تم الطلب",
                },
            },
            {
                key: "preparing",
                active: true,
                labels: {
                    fr: "En préparation",
                    en: "In preparation",
                    es: "En preparación",
                    it: "In preparazione",
                    pt: "Em preparação",
                    de: "In Vorbereitung",
                    ru: "Готовится",
                    ar: "قيد التحضير",
                },
            },
            {
                key: "ready",
                active: false,
                labels: {
                    fr: "Prêt",
                    en: "Ready",
                    es: "Listo",
                    it: "Pronto",
                    pt: "Pronto",
                    de: "Fertig",
                    ru: "Готово",
                    ar: "جاهز",
                },
            },
            {
                key: "served",
                active: false,
                labels: {
                    fr: "Servi",
                    en: "Served",
                    es: "Servido",
                    it: "Servito",
                    pt: "Servido",
                    de: "Serviert",
                    ru: "Подано",
                    ar: "تم التقديم",
                },
            },
        ],
    },

    advertising: {
        adsEnabled: true,
        sectionTitleVisible: true,
        selectedIcon: "tag",
        customIconUrl: "",
        displayType: "desktop",
        sectionTitles: {
            fr: "Publicité",
            en: "Advertising",
            ru: "Реклама",
            it: "Pubblicità",
            es: "Publicidad",
            pt: "Publicidade",
            de: "Werbung",
            ar: "إعلانات",
        },
        ads: { Mobile: [], Desktop: [] },
    },

    miscellaneousSettings: {
        primaryColor: "#c8a97e",
        secondaryColor: "#333333",
        nameVisible: true,
        descriptionVisible: true,
        restaurantTexts: {
            fr: {
                title: "Le Gourmet Parisien",
                description: "Cuisine française raffinée",
            },
            en: {
                title: "Le Gourmet Parisien",
                description: "Refined French cuisine",
            },
            ru: {
                title: "Le Gourmet Parisien",
                description: "Изысканная французская кухня",
            },
            it: {
                title: "Le Gourmet Parisien",
                description: "Cucina francese raffinata",
            },
            es: {
                title: "Le Gourmet Parisien",
                description: "Cocina francesa refinada",
            },
            pt: {
                title: "Le Gourmet Parisien",
                description: "Cozinha francesa refinada",
            },
            de: {
                title: "Le Gourmet Parisien",
                description: "Gehobene französische Küche",
            },
            ar: {
                title: "Le Gourmet Parisien",
                description: "المطبخ الفرنسي الراقي",
            },
        },
        showSocialNetworks: true,
        socialChannels: [
            { id: "instagram", enabled: true, url: "" },
            { id: "facebook", enabled: false, url: "" },
            { id: "tiktok", enabled: false, url: "" },
            { id: "twitter", enabled: false, url: "" },
            { id: "linkedin", enabled: false, url: "" },
            { id: "youtube", enabled: false, url: "" },
        ],
        showLegalInfo: true,
        enabledLanguages: {
            fr: true,
            en: true,
            ru: true,
            it: true,
            es: true,
            pt: true,
            de: true,
            ar: true,
        },
        defaultLanguage: "fr",
    },

    paymentOptions: {
        paymentEnabled: true,
        paymentMethods: [
            {
                id: "online",
                enabled: true,
                icon: "circle",
                title: {
                    fr: "Paiement en ligne",
                    en: "Online Payment",
                    ru: "Онлайн оплата",
                    it: "Pagamento online",
                    es: "Pago en línea",
                    pt: "Pagamento online",
                    de: "Online-Zahlung",
                    ar: "الدفع عبر الإنترنت",
                },
                description: {
                    fr: "Payez directement depuis votre téléphone",
                    en: "Pay directly from your phone",
                    ru: "Платите прямо с телефона",
                    it: "Paga direttamente dal telefono",
                    es: "Pague directamente desde su teléfono",
                    pt: "Pague diretamente do seu telefone",
                    de: "Bezahlen Sie direkt vom Handy",
                    ar: "ادفع مباشرة من هاتفك",
                },
            },
            {
                id: "tpe",
                enabled: true,
                icon: "card",
                title: {
                    fr: "Paiement par carte/TPE",
                    en: "Card Payment/TPE",
                    ru: "Оплата картой/TPE",
                    it: "Pagamento con carta/TPE",
                    es: "Pago con tarjeta/TPE",
                    pt: "Pagamento por cartão/TPE",
                    de: "Kartenzahlung/TPE",
                    ar: "الدفع بالبطاقة/TPE",
                },
                description: {
                    fr: "Utilisez le terminal de paiement",
                    en: "Use the payment terminal",
                    ru: "Используйте платёжный терминал",
                    it: "Usa il terminale di pagamento",
                    es: "Use el terminal de pago",
                    pt: "Use o terminal de pagamento",
                    de: "Nutzen Sie das Bezahlterminal",
                    ar: "استخدم محطة الدفع",
                },
            },
            {
                id: "cash",
                enabled: true,
                icon: "money",
                title: {
                    fr: "Paiement en espèces",
                    en: "Cash Payment",
                    ru: "Оплата наличными",
                    it: "Pagamento in contanti",
                    es: "Pago en efectivo",
                    pt: "Pagamento em dinheiro",
                    de: "Barzahlung",
                    ar: "الدفع نقدا",
                },
                description: {
                    fr: "Payez en espèces au comptoir",
                    en: "Pay with cash at the counter",
                    ru: "Оплата наличными на кассе",
                    it: "Paga in contanti al bancone",
                    es: "Pague en efectivo en el mostrador",
                    pt: "Pague em dinheiro no balcão",
                    de: "Barzahlung an der Theke",
                    ar: "ادفع نقدا في الكاونتر",
                },
            },
        ],
        splitPaymentEnabled: true,
        splitOptions: [
            {
                id: "equal",
                enabled: true,
                icon: "users",
                title: {
                    fr: "Diviser également",
                    en: "Split Equally",
                    ru: "Разделить поровну",
                    it: "Dividere equamente",
                    es: "Dividir igualmente",
                    pt: "Dividir igualmente",
                    de: "Gleichmäßig teilen",
                    ar: "تقسيم بالتساوي",
                },
                description: {
                    fr: "Partagez l'addition en parts égales",
                    en: "Share the bill equally",
                    ru: "Разделите счёт поровну",
                    it: "Dividi il conto in parti uguali",
                    es: "Comparta la cuenta en partes iguales",
                    pt: "Divida a conta em partes iguais",
                    de: "Teilen Sie die Rechnung gleichmäßig",
                    ar: "شارك الفاتورة بالتساوي",
                },
            },
            {
                id: "by_item",
                enabled: true,
                icon: "list",
                title: {
                    fr: "Par article",
                    en: "By Item",
                    ru: "По позициям",
                    it: "Per articolo",
                    es: "Por artículo",
                    pt: "Por artigo",
                    de: "Nach Artikel",
                    ar: "حسب المنتج",
                },
                description: {
                    fr: "Choisissez les articles à payer",
                    en: "Choose items to pay for",
                    ru: "Выберите позиции для оплаты",
                    it: "Scegli gli articoli da pagare",
                    es: "Elija los artículos a pagar",
                    pt: "Escolha os artigos a pagar",
                    de: "Wählen Sie die Artikel",
                    ar: "اختر المنتجات للدفع",
                },
            },
            {
                id: "custom",
                enabled: true,
                icon: "edit",
                title: {
                    fr: "Montant personnalisé",
                    en: "Custom Amount",
                    ru: "Произвольная сумма",
                    it: "Importo personalizzato",
                    es: "Monto personalizado",
                    pt: "Valor personalizado",
                    de: "Benutzerdefinierter Betrag",
                    ar: "مبلغ مخصص",
                },
                description: {
                    fr: "Entrez un montant personnalisé",
                    en: "Enter a custom amount",
                    ru: "Введите произвольную сумму",
                    it: "Inserisci un importo personalizzato",
                    es: "Ingrese un monto personalizado",
                    pt: "Insira um valor personalizado",
                    de: "Geben Sie einen Betrag ein",
                    ar: "أدخل مبلغا مخصصا",
                },
            },
        ],
        methodDescVisible: { online: true, tpe: true, cash: true },
        splitDescVisible: { equal: true, by_item: true, custom: true },
    },

    serverCallOptions: {
        serverCallEnabled: true,
        customMessageEnabled: true,
        quickOptions: [
            {
                id: "water",
                icon: "restaurant",
                isDefault: true,
                enabled: true,
                title: {
                    fr: "De l'eau",
                    en: "Water",
                    ru: "Воду",
                    it: "Acqua",
                    es: "Agua",
                    pt: "Água",
                    de: "Wasser",
                    ar: "ماء",
                },
            },
            {
                id: "bill",
                icon: "receipt",
                isDefault: true,
                enabled: true,
                title: {
                    fr: "L'addition",
                    en: "The Bill",
                    ru: "Счёт",
                    it: "Il conto",
                    es: "La cuenta",
                    pt: "A conta",
                    de: "Die Rechnung",
                    ar: "الفاتورة",
                },
            },
            {
                id: "cutlery",
                icon: "restaurant",
                isDefault: true,
                enabled: true,
                title: {
                    fr: "Des couverts",
                    en: "Extra Cutlery",
                    ru: "Приборы",
                    it: "Posate",
                    es: "Cubiertos",
                    pt: "Talheres",
                    de: "Besteck",
                    ar: "أدوات المائدة",
                },
            },
            {
                id: "clean",
                icon: "check",
                isDefault: true,
                enabled: true,
                title: {
                    fr: "Nettoyer la table",
                    en: "Clean the Table",
                    ru: "Убрать стол",
                    it: "Pulire il tavolo",
                    es: "Limpiar la mesa",
                    pt: "Limpar a mesa",
                    de: "Tisch reinigen",
                    ar: "تنظيف الطاولة",
                },
            },
            {
                id: "sauce",
                icon: "restaurant",
                isDefault: true,
                enabled: true,
                title: {
                    fr: "Sauce/Condiment",
                    en: "Sauce/Condiment",
                    ru: "Соус/Приправа",
                    it: "Salsa/Condimento",
                    es: "Salsa/Condimento",
                    pt: "Molho/Condimento",
                    de: "Sauce/Gewürz",
                    ar: "صلصة/بهار",
                },
            },
            {
                id: "other",
                icon: "help",
                isDefault: true,
                enabled: true,
                title: {
                    fr: "Autre demande",
                    en: "Other Request",
                    ru: "Другой запрос",
                    it: "Altra richiesta",
                    es: "Otra solicitud",
                    pt: "Outro pedido",
                    de: "Andere Anfrage",
                    ar: "طلب آخر",
                },
            },
        ],
    },
};

// ─── Slice ───────────────────────────────────────────────
const scanToSlice = createSlice({
    name: "scanTo",
    initialState,
    reducers: {
        // ── Global ──────────────────────────────────────
        setIsEdited(state, action: PayloadAction<boolean>) {
            state.isEdited = action.payload;
        },
        setActiveTab(state, action: PayloadAction<number>) {
            state.activeTab = action.payload;
        },
        // Set the single language the whole preview renders in. Dispatched
        // by every rubric's language picker so any pick flips all sections.
        setPreviewLanguage(state, action: PayloadAction<string>) {
            state.previewLanguage = action.payload;
        },
        resetScanTo() {
            // Return a fresh deep clone instead of the module-level constant
            // so two consecutive resets produce DISTINCT references —
            // reference-equality subscribers (e.g. selectors with shallow
            // identity checks) would otherwise miss the reset transition.
            return JSON.parse(JSON.stringify(initialState));
        },

        // ── Activity ────────────────────────────────────
        setActivity(state, action: PayloadAction<Partial<ActivityConfig>>) {
            Object.assign(state.activity, action.payload);
        },

        // ── Banner ──────────────────────────────────────
        setBanner(state, action: PayloadAction<Partial<BannerConfig>>) {
            const payload = { ...action.payload };
            // Defensive: drop images entirely when null/undefined so a
            // malformed API payload cannot overwrite a valid structure.
            if (payload.images == null) {
                delete payload.images;
            } else if (Array.isArray(payload.images)) {
                // Migrate old flat-array images to new { Mobile, Desktop } structure
                const flatImages: BannerImageItem[] = payload.images;
                payload.images = { Mobile: [], Desktop: flatImages };
            }
            Object.assign(state.banner, payload);
        },

        // ── Offers ──────────────────────────────────────
        setOffers(state, action: PayloadAction<Partial<OffersConfig>>) {
            // Preview senders include a `fullOffers` extension that is not part
            // of OffersConfig; type the local payload precisely instead of `any`.
            const payload: Partial<OffersConfig> & {
                fullOffers?: OfferItem[] | OfferImages;
                previewOffers?: any[];
            } = { ...action.payload };
            // The Offres broadcast carries TWO views of the data:
            //   • `payload.offers` — flat, translated, active-filtered, with
            //     resolved imageUrl (blob URLs included). This is the runtime
            //     view other rubrics' previews need to render.
            //   • `payload.fullOffers` — the { Mobile, Desktop } persistence
            //     shape with per-language OfferItem objects.
            // Preserve the flat preview array under `previewOffers` BEFORE we
            // overwrite `offers` with the persistence shape — otherwise the
            // resolved blob URLs (from local `uploadedImages`) are lost and
            // other rubrics fall back to placeholder images.
            if (payload.fullOffers) {
                if (
                    Array.isArray(payload.offers) &&
                    payload.previewOffers === undefined
                ) {
                    payload.previewOffers = payload.offers as any[];
                }
                const raw = payload.fullOffers;
                payload.offers = Array.isArray(raw)
                    ? { Mobile: [], Desktop: raw }
                    : raw;
                delete payload.fullOffers;
            } else if (payload.offers && Array.isArray(payload.offers)) {
                // Migrate old flat-array from API
                const flatOffers: OfferItem[] = payload.offers;
                payload.offers = { Mobile: [], Desktop: flatOffers };
            }
            Object.assign(state.offers, payload);
        },
        updateOffer(
            state,
            action: PayloadAction<{
                displayKey: "Mobile" | "Desktop";
                offer: OfferItem;
            }>
        ) {
            const list = state.offers.offers[action.payload.displayKey];
            const idx = list.findIndex((o) => o.id === action.payload.offer.id);
            if (idx !== -1) list[idx] = action.payload.offer;
        },

        // ── Actions ─────────────────────────────────────
        setActions(state, action: PayloadAction<Partial<ActionsConfig>>) {
            Object.assign(state.actions, action.payload);
        },
        updateAction(state, action: PayloadAction<ActionItem>) {
            const idx = state.actions.actions.findIndex(
                (a) => a.id === action.payload.id
            );
            if (idx !== -1) state.actions.actions[idx] = action.payload;
        },

        // ── My Order ────────────────────────────────────
        setMyOrder(state, action: PayloadAction<Partial<MyOrderConfig>>) {
            Object.assign(state.myOrder, action.payload);
        },

        // ── Advertising ─────────────────────────────────
        setAdvertising(
            state,
            action: PayloadAction<Partial<AdvertisingConfig>>
        ) {
            // Preview senders include a `fullAds` extension that is not part
            // of AdvertisingConfig; type the local payload precisely.
            const payload: Partial<AdvertisingConfig> & {
                fullAds?: AdItem[] | AdImages;
            } = { ...action.payload };
            if (payload.fullAds) {
                const raw = payload.fullAds;
                payload.ads = Array.isArray(raw)
                    ? { Mobile: [], Desktop: raw }
                    : raw;
                delete payload.fullAds;
            } else if (payload.ads && Array.isArray(payload.ads)) {
                const flatAds: AdItem[] = payload.ads;
                payload.ads = { Mobile: [], Desktop: flatAds };
            }
            Object.assign(state.advertising, payload);
        },

        // ── Miscellaneous Settings ──────────────────────
        setMiscellaneous(
            state,
            action: PayloadAction<Partial<MiscellaneousConfig>>
        ) {
            Object.assign(state.miscellaneousSettings, action.payload);
        },

        // ── Payment Options ─────────────────────────────
        setPaymentOptions(
            state,
            action: PayloadAction<Partial<PaymentConfig>>
        ) {
            Object.assign(state.paymentOptions, action.payload);
        },

        // ── Server Call Options ─────────────────────────
        setServerCallOptions(
            state,
            action: PayloadAction<Partial<ServerCallConfig>>
        ) {
            Object.assign(state.serverCallOptions, action.payload);
        },
    },
});

export const {
    setIsEdited,
    setActiveTab,
    setPreviewLanguage,
    resetScanTo,
    setActivity,
    setBanner,
    setOffers,
    updateOffer,
    setActions,
    updateAction,
    setMyOrder,
    setAdvertising,
    setMiscellaneous,
    setPaymentOptions,
    setServerCallOptions,
} = scanToSlice.actions;

export default scanToSlice.reducer;
